import React, { useState, useMemo, useRef, useEffect } from 'react';
import { IMessageOptions } from 'react-chatbot-kit/build/src/interfaces/IMessages';
import ChatbotLoader from '../widgets/ChatbotLoader';

const AvannisResponseMessage = (props: IMessageOptions) => {
    const { payload } = props;
    const [copied, setCopied] = useState(false);
    const messageRef = useRef<HTMLDivElement>(null);

    // Function to open coaching form
    const openCoachingForm = (id: string, survey: string = '') => {
        const page = `coaching_form?back=smart_summaries&id=${id}&survey=${survey}`;
        window.open(page, '', 'width=800,height=800,scrollbars=1,resizable=1');
    };

    // Process the message to make survey IDs clickable
    const processedMessage = useMemo(() => {
        if (!payload.message) return '';

        // Replace patterns like (survey: ABC, id: 12345) or (id: 12345, survey: ABC) with clickable links
        let processed = payload.message;

        // Pattern 1: (survey: ABC, id: 12345) or (id: 12345, survey: ABC)
        processed = processed.replace(
            /\((survey:\s*([^,]+),\s*id:\s*(\d+)|id:\s*(\d+),\s*survey:\s*([^)]+))\)/gi,
            (match, p1, survey1, id1, id2, survey2) => {
                const id = id1 || id2;
                const survey = survey1 || survey2;
                return `<a href="#" class="coaching-form-link" data-id="${id}" data-survey="${survey}" style="color: #fff; text-decoration: underline; cursor: pointer;">(survey: ${survey}, id: ${id})</a>`;
            }
        );

        // Pattern 2: (id: 12345) - fallback for when only ID is present
        processed = processed.replace(
            /\(id:\s*(\d+)\)/gi,
            (match, id) => {
                return `<a href="#" class="coaching-form-link" data-id="${id}" data-survey="" style="color: #fff; text-decoration: underline; cursor: pointer;">(id: ${id})</a>`;
            }
        );

        return processed;
    }, [payload.message]);

    // Add click event listeners to all coaching form links after render
    useEffect(() => {
        if (messageRef.current) {
            const links = messageRef.current.querySelectorAll('.coaching-form-link');

            const handleClick = (e: Event) => {
                e.preventDefault();
                const target = e.currentTarget as HTMLAnchorElement;
                const id = target.getAttribute('data-id');
                const survey = target.getAttribute('data-survey') || '';
                if (id) {
                    openCoachingForm(id, survey);
                }
            };

            links.forEach(link => {
                link.addEventListener('click', handleClick);
            });

            // Cleanup event listeners
            return () => {
                links.forEach(link => {
                    link.removeEventListener('click', handleClick);
                });
            };
        }
    }, [processedMessage]);

    const handleCopy = () => {
        if (payload.message) {
            // Create a temporary element to extract text from HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = payload.message;
            const textContent = tempDiv.textContent || tempDiv.innerText || '';

            // Copy to clipboard
            navigator.clipboard.writeText(textContent).then(() => {
                setCopied(true);
                // Reset the checkmark back to copy icon after 2 seconds
                setTimeout(() => {
                    setCopied(false);
                }, 2000);
            }).catch((err) => {
                console.error('Failed to copy text: ', err);
            });
        }
    };

    return (
        <div className="react-chatbot-kit-chat-bot-message-container">
            <div className="react-chatbot-kit-chat-bot-avatar">
                <div className="react-chatbot-kit-chat-bot-avatar-container">
                    <img
                        src="/images/avanis-icon.png?v=2"
                        alt="Avannis"
                        style={{ width: 40, height: 40, borderRadius: '50%' }}
                    />
                </div>
            </div>
            <div
                className="react-chatbot-kit-chat-bot-message"
                style={{ backgroundColor: 'rgb(4, 147, 252)', position: 'relative' }}
            >
                {payload.isLoading ? <ChatbotLoader /> :
                    <>
                        <div style={{ paddingRight: '45px' }}>
                            <span
                                onClick={handleCopy}
                                style={{
                                    position: 'absolute',
                                    top: '5px',
                                    right: '8px',
                                    cursor: 'pointer',
                                    fontSize: '32px',
                                    color: copied ? '#4caf50' : 'rgba(255, 255, 255, 0.9)',
                                    transition: 'color 0.2s ease',
                                    lineHeight: '1',
                                    display: 'block'
                                }}
                                title={copied ? 'Copied!' : 'Copy to clipboard'}
                            >
                                {copied ? '✓' : '⎘'}
                            </span>
                            <div ref={messageRef} dangerouslySetInnerHTML={{ __html: processedMessage }}></div>
                        </div>
                    </>
                }
                <div
                    className="react-chatbot-kit-chat-bot-message-arrow"
                    style={{ borderRightColor: 'rgb(4, 147, 252)' }}
                ></div>
            </div>
        </div>
    );
};

export default AvannisResponseMessage;
